import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Calendar, MapPin, Clock, Users, Globe, Building2, 
  Video, ArrowRight, ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';

const upcomingMeetings = [
  {
    id: 1,
    title: 'OPAD Global Strategy Meeting 2026',
    date: 'March 15-17, 2026',
    time: '09:00 - 17:00 CET',
    location: 'Stockholm, Sweden',
    type: 'In-Person',
    description: 'Annual strategic planning meeting for OPAD leadership and regional directors.',
    category: 'Internal Meeting'
  },
  {
    id: 2,
    title: 'Partnership Development Workshop',
    date: 'April 5, 2026',
    time: '14:00 - 16:00 CET',
    location: 'Virtual (Zoom)',
    type: 'Online',
    description: 'Workshop for potential partners interested in collaborating with OPAD.',
    category: 'Workshop'
  },
  {
    id: 3,
    title: 'Youth Empowerment Forum',
    date: 'April 20-22, 2026',
    time: '10:00 - 18:00 WAT',
    location: 'Lagos, Nigeria',
    type: 'Hybrid',
    description: 'Regional forum on youth employment and skills development in West Africa.',
    category: 'Forum'
  }
];

const unhcrSessions = [
  {
    id: 1,
    title: '76th Session of the Executive Committee of UNHCR',
    date: 'October 2025',
    location: 'Geneva, Switzerland',
    status: 'Completed',
    description: 'OPAD participated in discussions on global refugee protection and solutions.',
    highlights: ['Statement delivered on climate-displaced populations', 'Side event on sustainable integration']
  },
  {
    id: 2,
    title: 'UNHCR Standing Committee Meeting',
    date: 'March 2026',
    location: 'Geneva, Switzerland',
    status: 'Upcoming',
    description: 'Regular standing committee meeting on international protection.',
    highlights: ['Budget review', 'Programme updates', 'NGO consultations']
  },
  {
    id: 3,
    title: '77th Session of the Executive Committee of UNHCR',
    date: 'October 2026',
    location: 'Geneva, Switzerland',
    status: 'Upcoming',
    description: 'Annual plenary session of UNHCR Executive Committee.',
    highlights: ['Global Compact on Refugees review', 'NGO statement coordination']
  }
];

const conferences = [
  {
    id: 1,
    title: 'UN Environment Assembly (UNEA-7)',
    date: 'December 2025',
    location: 'Nairobi, Kenya',
    status: 'Completed',
    description: 'OPAD delivered statement on climate action and sustainable development.',
    link: '/policy-engagement'
  },
  {
    id: 2,
    title: 'High-Level Political Forum on Sustainable Development',
    date: 'July 2026',
    location: 'New York, USA',
    status: 'Upcoming',
    description: 'Annual review of SDG progress at UN Headquarters.'
  },
  {
    id: 3,
    title: 'COP31 - UN Climate Change Conference',
    date: 'November 2026',
    location: 'TBD',
    status: 'Upcoming',
    description: 'OPAD will participate in climate negotiations and side events.'
  },
  {
    id: 4,
    title: 'World Humanitarian Summit Follow-up',
    date: 'September 2026',
    location: 'Geneva, Switzerland',
    status: 'Upcoming',
    description: 'Review of commitments made at the World Humanitarian Summit.'
  }
];

const regularMeetings = [
  {
    title: 'OPAD Board Meeting',
    frequency: 'Quarterly',
    description: 'Strategic oversight and governance meetings.',
    nextDate: 'Q1 2026'
  },
  {
    title: 'Regional Coordinators Call',
    frequency: 'Monthly',
    description: 'Coordination call for all regional offices.',
    nextDate: 'First Monday of each month'
  },
  {
    title: 'NGO Committee on Migration',
    frequency: 'Bi-monthly',
    description: 'Civil society coordination on migration issues.',
    nextDate: 'February 2026'
  },
  {
    title: 'SDG Coalition Meeting',
    frequency: 'Quarterly',
    description: 'Multi-stakeholder partnership for SDG implementation.',
    nextDate: 'March 2026'
  }
];

export default function EventsPage() {
  return (
    <div className="min-h-screen bg-[#FDF8F4]">
      {/* Hero Section */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#4A7C59] via-[#5A8C69] to-[#6A9C79]">
          <div className="absolute top-0 right-0 w-[500px] h-[450px] bg-white/10 rounded-bl-[250px]" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[350px] bg-[#E5B75F]/20 rounded-tr-[180px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Calendar className="w-16 h-16 text-white mx-auto mb-6" />
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-['Manrope'] text-white mb-6">
                Events & Meetings
              </h1>
              <p className="text-lg md:text-xl text-white/90 max-w-3xl mx-auto">
                Stay updated with OPAD's participation in international forums, conferences, 
                and our internal coordination meetings.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Event - EU CERV */}
      <section className="relative section-padding bg-gradient-to-br from-[#FDF8F4] to-white" data-testid="featured-eu-cerv">
        <div className="container-custom">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden"
            >
              <div className="grid lg:grid-cols-2 gap-0">
                <div className="relative h-64 lg:h-auto">
                  <img
                    src="https://customer-assets.emergentagent.com/job_10eec864-0b4d-4948-b760-389570b9159b/artifacts/ysjvsve9_PIC%2002.jpeg"
                    alt="OPAD EU CERV Project event"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4 bg-[#E5B75F] text-slate-900 px-3 py-1 rounded-full text-xs font-bold">
                    Featured Event
                  </div>
                </div>
                <div className="p-8 lg:p-10">
                  <span className="inline-block bg-[#7BC4E2]/15 text-[#3A8FB5] px-3 py-1 rounded-full text-xs font-semibold mb-3">
                    EU CERV Programme · Project No. 101249580
                  </span>
                  <h2 className="text-2xl md:text-3xl font-bold font-['Manrope'] text-slate-900 mb-3">
                    OPAD Participates in EU CERV Project — EcoActive Citizens for a Sustainable Europe
                  </h2>
                  <p className="text-slate-600 mb-4">
                    OPAD joins partners across Europe in the European Union-funded "Network of Towns" initiative promoting sustainability, civic engagement, and environmental responsibility.
                  </p>
                  <div className="space-y-3 text-sm">
                    <div className="border-l-4 border-[#4A7C59] pl-3">
                      <p className="font-semibold text-slate-900">Event I — Buarcos, Portugal</p>
                      <p className="text-slate-500">25–28 February 2026 · Sustainable coastal & marine ecosystem protection, Brotherhood Ceremony</p>
                    </div>
                    <div className="border-l-4 border-[#E08B7A] pl-3">
                      <p className="font-semibold text-slate-900">Event II — Santiago de Compostela, Spain</p>
                      <p className="text-slate-500">1–4 March 2026 · Circular economy, dialogue with Parliament of Galicia, women in green transition</p>
                    </div>
                  </div>
                  <div className="mt-6 p-4 bg-slate-50 rounded-xl">
                    <p className="text-xs text-slate-600">
                      <strong>OPAD's Role:</strong> Sharing best practices in community development & sustainability, contributing to climate resilience & social inclusion dialogues, and strengthening European and international networks aligned with the SDGs.
                    </p>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-1 p-1 bg-white">
                <img
                  src="https://customer-assets.emergentagent.com/job_10eec864-0b4d-4948-b760-389570b9159b/artifacts/y2vxos6y_PIC%201.jpeg"
                  alt="EU CERV partner gathering"
                  className="w-full h-48 md:h-64 object-cover rounded-xl"
                />
                <img
                  src="https://customer-assets.emergentagent.com/job_10eec864-0b4d-4948-b760-389570b9159b/artifacts/8x5f5ire_PIC%2005.jpeg"
                  alt="EU CERV brotherhood ceremony"
                  className="w-full h-48 md:h-64 object-cover rounded-xl"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Upcoming Meetings Section */}
      <section className="relative section-padding overflow-hidden" data-testid="upcoming-meetings">
        <div className="absolute inset-0 bg-white">
          <div className="absolute top-0 left-0 w-[300px] h-[250px] bg-[#7BC4E2]/10 rounded-br-[150px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-[#7BC4E2]/20 rounded-full flex items-center justify-center">
              <Calendar className="w-6 h-6 text-[#7BC4E2]" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold font-['Manrope'] text-slate-900">
              Upcoming Meetings
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {upcomingMeetings.map((meeting, index) => (
              <motion.div
                key={meeting.id}
                className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden hover:shadow-xl transition-shadow"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      meeting.type === 'In-Person' ? 'bg-[#4A7C59]/10 text-[#4A7C59]' :
                      meeting.type === 'Online' ? 'bg-[#7BC4E2]/10 text-[#7BC4E2]' :
                      'bg-[#E5B75F]/10 text-[#E5B75F]'
                    }`}>
                      {meeting.type}
                    </span>
                    <span className="text-xs text-slate-500">{meeting.category}</span>
                  </div>
                  <h3 className="font-bold text-slate-900 mb-3">{meeting.title}</h3>
                  <div className="space-y-2 text-sm text-slate-600 mb-4">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-[#E08B7A]" />
                      <span>{meeting.date}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-[#E08B7A]" />
                      <span>{meeting.time}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-[#E08B7A]" />
                      <span>{meeting.location}</span>
                    </div>
                  </div>
                  <p className="text-sm text-slate-500">{meeting.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* UNHCR Sessions Section */}
      <section className="relative section-padding overflow-hidden bg-slate-50" data-testid="unhcr-sessions">
        <div className="container-custom">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-[#E08B7A]/20 rounded-full flex items-center justify-center">
              <Globe className="w-6 h-6 text-[#E08B7A]" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold font-['Manrope'] text-slate-900">
              Sessions of UNHCR
            </h2>
          </div>

          <div className="space-y-6">
            {unhcrSessions.map((session, index) => (
              <motion.div
                key={session.id}
                className="bg-white rounded-2xl shadow-lg border border-slate-100 p-6"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="flex flex-col md:flex-row md:items-start gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-bold text-lg text-slate-900">{session.title}</h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        session.status === 'Completed' ? 'bg-green-100 text-green-700' : 'bg-[#E5B75F]/20 text-[#E5B75F]'
                      }`}>
                        {session.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-slate-500 mb-3">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {session.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {session.location}
                      </span>
                    </div>
                    <p className="text-slate-600 mb-3">{session.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {session.highlights.map((highlight, i) => (
                        <span key={i} className="bg-slate-100 text-slate-600 px-3 py-1 rounded-full text-xs">
                          {highlight}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Conferences Section */}
      <section className="relative section-padding overflow-hidden" data-testid="conferences">
        <div className="container-custom">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-[#4A7C59]/20 rounded-full flex items-center justify-center">
              <Building2 className="w-6 h-6 text-[#4A7C59]" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold font-['Manrope'] text-slate-900">
              Conferences
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {conferences.map((conf, index) => (
              <motion.div
                key={conf.id}
                className="bg-white rounded-2xl shadow-lg border border-slate-100 p-6 hover:shadow-xl transition-shadow"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="flex items-start justify-between mb-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    conf.status === 'Completed' ? 'bg-green-100 text-green-700' : 'bg-[#7BC4E2]/20 text-[#7BC4E2]'
                  }`}>
                    {conf.status}
                  </span>
                  {conf.link && (
                    <Link to={conf.link} className="text-[#7BC4E2] hover:underline text-sm flex items-center gap-1">
                      View Statement <ExternalLink className="w-3 h-3" />
                    </Link>
                  )}
                </div>
                <h3 className="font-bold text-lg text-slate-900 mb-2">{conf.title}</h3>
                <div className="flex items-center gap-4 text-sm text-slate-500 mb-3">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {conf.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {conf.location}
                  </span>
                </div>
                <p className="text-slate-600">{conf.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Regular Meetings Section */}
      <section className="relative section-padding overflow-hidden bg-[#4A7C59]/5" data-testid="regular-meetings">
        <div className="container-custom">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-[#E5B75F]/20 rounded-full flex items-center justify-center">
              <Users className="w-6 h-6 text-[#E5B75F]" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold font-['Manrope'] text-slate-900">
              Regular Meetings
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {regularMeetings.map((meeting, index) => (
              <motion.div
                key={meeting.title}
                className="bg-white rounded-2xl shadow-lg border border-slate-100 p-6"
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="text-[#E5B75F] font-semibold text-sm mb-2">{meeting.frequency}</div>
                <h3 className="font-bold text-slate-900 mb-2">{meeting.title}</h3>
                <p className="text-sm text-slate-500 mb-3">{meeting.description}</p>
                <div className="text-xs text-slate-400">
                  Next: {meeting.nextDate}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#7BC4E2] via-[#8BCDE8] to-[#9BD6EE]">
          <div className="absolute top-0 right-0 w-[400px] h-[350px] bg-white/10 rounded-bl-[200px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <Video className="w-12 h-12 text-white mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-white mb-6">
              Want to Attend or Collaborate?
            </h2>
            <p className="text-white/90 mb-8">
              If you're interested in attending our events or collaborating with OPAD at international forums, 
              please get in touch with our team.
            </p>
            <Link to="/contact">
              <Button 
                size="lg" 
                className="bg-white text-[#7BC4E2] hover:bg-white/90 rounded-full px-10 py-6 text-lg font-semibold"
              >
                Contact Us
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
